import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { store } from "@/redux/store";
import {
  selectTerminals,
  selectProducts,
  setProducts,
  setTerminals,
  selectCountryRegionDomains,
  setCountryRegionDomains,
  selectFaultCodes,
  setFaultCodes,
} from "@/redux/shareSlice";
import { CommonApi } from "@/services/Common.api";
import { BusinessHelper } from "@/shared/utils/Business.helper";
import { isAdmin } from "@/shared/utils/roles";
import { Utils } from "@/shared/utils/Utils";
import { useAuth } from "@/context/AuthContext";

export const useCommonData = () => {
  const { userRoles, userDomain } = useAuth();
  const terminals = useSelector(selectTerminals);
  const products = useSelector(selectProducts);
  const countryRegionDomains = useSelector(selectCountryRegionDomains);
  const faultCodes = useSelector(selectFaultCodes);

  const filteredcountryRegionDomains = useMemo(
    () =>
      isAdmin(userRoles) || Utils.isEmpty(userDomain)
        ? countryRegionDomains
        : countryRegionDomains.filter((data) => data.domain === userDomain),
    [userRoles, userDomain, countryRegionDomains]
  );

  const regionOptions = useMemo(() => {
    var uniqueRegions = Array.from(new Set(filteredcountryRegionDomains.map((c) => c.region)));
    return uniqueRegions.map((region) => ({ value: region, label: region }));
  }, [filteredcountryRegionDomains]);

  const domainOptions = useMemo(
    () => Array.from(new Set(countryRegionDomains.map((cd) => cd.domain))).filter(Boolean),
    [countryRegionDomains]
  );

  const countryOptions = useMemo(() => {
    const uniqueCountriesMap = new Map<string, string>();
    filteredcountryRegionDomains.forEach((c) => {
      if (!uniqueCountriesMap.has(c.countryCode)) {
        uniqueCountriesMap.set(c.countryCode, c.countryName);
      }
    });

    return Array.from(uniqueCountriesMap.entries()).map(([countryCode, countryName]) => ({
      value: countryCode,
      label: countryName,
    }));
  }, [filteredcountryRegionDomains]);

  const faultCodesCodeList = useMemo(() => faultCodes.map((fc) => fc.code), [faultCodes]);

  const filteredTerminals = useMemo(() => {
    var countryCodes = countryOptions.map((option) => option.value);
    return terminals.filter((item) => countryCodes.includes(item.countryCode));
  }, [countryOptions, terminals]);

  const loadCommonData = useCallback(async () => {
    if (countryRegionDomains.length === 0 || terminals.length === 0 || products.length === 0 || faultCodes.length === 0) {
      const [terminalsData, productsData, CountryRegionDomainsData, faultCodesData] = await Promise.all([
        CommonApi.getTerminals(),
        CommonApi.getProducts(),
        CommonApi.getCountryRegionDomains(),
        CommonApi.getFaultCodes(),
      ]);
      store.dispatch(setTerminals(terminalsData));
      store.dispatch(setProducts(productsData));
      store.dispatch(setCountryRegionDomains(CountryRegionDomainsData));
      store.dispatch(setFaultCodes(faultCodesData));
    }
  }, [countryRegionDomains.length, terminals.length, products.length, faultCodes.length]);

  const reloadFaultCodes = useCallback(async () => {
    const faultCodesData = await CommonApi.getFaultCodes();
    store.dispatch(setFaultCodes(faultCodesData));
  }, []);

  const getFaultCodeWorkOrderInstructions = useCallback(
    (faultCode: string) => {
      const selectedFaultCode = faultCodes.find((item) => item.code === faultCode);
      if (!selectedFaultCode) {
        return "";
      }

      return BusinessHelper.IsNADomain(userDomain) ? selectedFaultCode.workOrderInstructionsNA : selectedFaultCode.workOrderInstructionsEU;
    },
    [faultCodes, userDomain]
  );

  return {
    terminals: filteredTerminals,
    products,
    countryRegionDomains,
    faultCodes,
    faultCodesCodeList,
    regionOptions,
    countryOptions,
    domainOptions,
    loadCommonData,
    reloadFaultCodes,
    getFaultCodeWorkOrderInstructions,
  };
};
